<script lang="ts" setup>
import { computed, toRefs, withDefaults } from 'vue';
import { useDisplay } from 'vuetify';

type Props = {
  isSpread?: boolean;
  isContinuous?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  isSpread: false,
  isContinuous: false,
});
const { isSpread, isContinuous } = toRefs(props);

const { mdAndUp, smAndDown } = useDisplay();

const getRandomInt = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min;

const minTextLines = 15;
const customWidthClasses = ['w-100', 'w-90', 'w-80', 'w-85', 'w-83', 'w-75', 'w-75', 'w-66', 'w-50', 'w-60'];

const generatePageBlocks = () => {
  const blocks: { type: 'image' | 'subheader' | 'text'; class?: string }[] = [];

  const imageStyle = getRandomInt(0, 2);
  const numSubheadings = getRandomInt(0, 3);
  let linesCounter = minTextLines + getRandomInt(-5, 5);
  let subheadingCounter = 0;

  if (imageStyle === 2 && getRandomInt(0, 1) === 0) {
    // Custom height classes used for images
    blocks.push({ type: 'image', class: 'h-64 h-sm-80 h-lg-96' });
    linesCounter = getRandomInt(1, 4);
  } else if (imageStyle === 1) {
    blocks.push({ type: 'image', class: 'h-28 h-sm-40' });
  }

  let textLinesInserted = 0;
  while (textLinesInserted < linesCounter) {
    if (subheadingCounter < numSubheadings && textLinesInserted > 3 && getRandomInt(0, 5) === 0) {
      blocks.push({ type: 'subheader' });
      subheadingCounter++;
    }
    const randomIndex = getRandomInt(0, customWidthClasses.length - 1);
    blocks.push({ type: 'text', class: customWidthClasses[randomIndex] });
    textLinesInserted++;
  }

  return blocks;
};

const pageCount = computed(() => (isSpread.value ? 2 : 1));

const pageLayouts = computed(() => {
  const layouts = [];
  for (let p = 0; p < pageCount.value; p++) {
    layouts.push({ id: p, contentBlocks: generatePageBlocks() });
  }
  return layouts;
});
</script>

<template>
  <div class="h-screen w-100 d-flex align-center justify-center pa-4 pa-sm-8 font-inter">
    <div
      class="skeleton-frame w-100 flex-shrink-0 transition-all duration-500 ease-in-out"
      :class="{
        'lg-w-90-custom': isSpread && mdAndUp,
        'max-w-xl-custom mx-auto': !isSpread && smAndDown,
      }"
    >
      <div
        id="viewer-skeleton"
        tabindex="-1"
        class="skeleton-view pa-md-12 pa-lg-16 grid gap-8"
        :class="{
          'lg-grid-cols-2': isSpread,
          'grid-cols-1': !isSpread,
          'h-full-viewport overflow-y-scroll': isContinuous,
          'h-full max-h-900-custom': !isContinuous && !smAndDown,
        }"
      >
        <div
          v-for="layout in pageLayouts"
          :key="layout.id"
          class="skeleton-layout w-100 pa-4 rounded-sm transition-opacity duration-300 position-relative"
        >
          <template v-if="layout.id === 0">
            <div class="skeleton-content text-h2 h-6 w-75 rounded-sm mb-3"></div>
            <div class="skeleton-content text-h4 h-4 w-50 rounded-sm mb-6"></div>
          </template>

          <template v-for="(block, index) in layout.contentBlocks" :key="index">
            <div v-if="block.type === 'subheader'" class="skeleton-content h-4 text-h4 w-33 rounded-sm mb-4 mt-6"></div>

            <div
              v-else-if="block.type === 'image'"
              :class="block.class"
              class="skeleton-content w-100 rounded-sm mb-4"
            ></div>

            <div
              v-else-if="block.type === 'text'"
              :class="block.class"
              class="skeleton-content h-3 text-body-1 rounded-sm mb-2"
            ></div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

$breakpoint-md-up: map.get(v.$display-breakpoints, 'md-and-up');
$bar-top-height: 64px;
$bar-bottom-height: 56px;
$bar-bottom-mobile-height: 128px;

.skeleton-content {
  position: relative;
  overflow: hidden;
  background-color: #dddddd;
  animation: pulse-bg 1.5s ease-in-out infinite;
}

.skeleton-content::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transform: translateX(-100%);

  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0,
    rgba(255, 255, 255, 0.2) 20%,
    rgba(255, 255, 255, 0.5) 60%,
    rgba(255, 255, 255, 0)
  );

  /* The animation that moves the shimmer */
  animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes pulse-bg {
  0% {
    background-color: #dddddd;
  }
  50% {
    background-color: #d0d0d0;
  }
  100% {
    background-color: #dddddd;
  }
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-content {
    animation: none;
  }

  .skeleton-content::after {
    animation: none;
  }
}

.h-full-viewport {
  height: calc(100vh - #{$bar-top-height + $bar-bottom-height});
}

.max-w-md-custom {
  max-width: 38rem;
}
.max-w-xl-custom {
  max-width: 46rem;
}

.grid {
  display: grid;
}
.gap-8 {
  gap: 2rem;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, 1fr);
}
.lg-grid-cols-2 {
  @media #{$breakpoint-md-up} {
    grid-template-columns: repeat(2, 1fr);
  }
}

.w-100 {
  width: 100%;
}
.w-90 {
  width: 90%;
}
.w-85 {
  width: 85%;
}
.w-83 {
  width: 83.3333%;
}
.w-80 {
  width: 80%;
}
.w-75 {
  width: 75%;
}
.w-66 {
  width: 66.6667%;
}
.w-60 {
  width: 60%;
}
.w-50 {
  width: 50%;
}
.w-33 {
  width: 33.3333%;
}

.h-3 {
  height: 0.75rem;
}
.h-4 {
  height: 1rem;
}
.h-6 {
  height: 1.5rem;
}
.h-64 {
  height: 16rem;
}
.h-28 {
  height: 7rem;
}

@media #{map.get(v.$display-breakpoints, 'sm-and-up')} {
  .h-sm-40 {
    height: 20rem;
  }
  .h-sm-80 {
    height: 40rem;
  }
}
@media #{$breakpoint-md-up} {
  .h-lg-96 {
    height: 24rem;
  }
}

.lg-w-90-custom {
  @media #{$breakpoint-md-up} {
    width: 90%;
  }
}
</style>
